import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentStatus({ type }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart, refreshUser } = useApp();
  const [status, setStatus] = useState(type === "cancel" ? "cancelled" : "checking");
  const pollCount = useRef(0);

  useEffect(() => {
    if (type === "cancel") return;
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (pollCount.current >= 10) {
        setStatus("timeout");
        return;
      }
      pollCount.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          clearCart();
          refreshUser();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      active = false;
    };
  }, [type, params, clearCart, refreshUser]);

  const Wrap = ({ children }) => (
    <div className="max-w-xl mx-auto px-4 py-24 text-center">{children}</div>
  );

  if (status === "checking") {
    return (
      <Wrap>
        <Loader2 size={56} className="mx-auto text-[#E63946] animate-spin mb-5" />
        <h1 className="font-display text-2xl font-bold">Confirming your payment...</h1>
        <p className="text-slate-500 mt-2">Please wait, this only takes a moment.</p>
      </Wrap>
    );
  }

  if (status === "success") {
    return (
      <Wrap>
        <CheckCircle2 size={72} className="mx-auto text-emerald-500 mb-5" data-testid="payment-success-icon" />
        <h1 className="font-display text-3xl font-bold">Order Confirmed! 🎉</h1>
        <p className="text-slate-500 mt-3">
          Thank you for shopping with Bull Creations. Your order is being prepared in Tiruppur and will ship soon.
        </p>
        <div className="flex gap-3 justify-center mt-8">
          <Button onClick={() => navigate("/orders")} className="bg-[#1D3557] hover:bg-[#162943]" data-testid="view-orders-button">
            View My Orders
          </Button>
          <Button onClick={() => navigate("/shop")} variant="outline">Continue Shopping</Button>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <XCircle size={72} className="mx-auto text-[#E63946] mb-5" />
      <h1 className="font-display text-2xl font-bold">
        {status === "cancelled" ? "Payment Cancelled" : "Payment Not Completed"}
      </h1>
      <p className="text-slate-500 mt-3">
        {status === "cancelled"
          ? "Your payment was cancelled. Your bag is still saved."
          : "We couldn't confirm your payment. If money was deducted, it will be refunded."}
      </p>
      <div className="flex gap-3 justify-center mt-8">
        <Link to="/checkout"><Button className="bg-[#E63946] hover:bg-[#d12836]">Try Again</Button></Link>
        <Link to="/shop"><Button variant="outline">Back to Shop</Button></Link>
      </div>
    </Wrap>
  );
}
